import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SnackbarComponent } from 'src/app/core/components/snackbar/snackbar.component';
import { ThemeService } from 'src/app/core/services/theme.service';
import { ExportState, ExportStatus } from '../../models/ticket-analysis.models';
import { TicketAnalysisService } from '../../services/ticket-analysis.service';

@Component({
  selector: 'ticket-analysis',
  templateUrl: './ticket-analysis.component.html',
  styleUrls: ['./ticket-analysis.component.scss'],
  standalone: false,
})
export class TicketAnalysisComponent implements OnInit {
  exportState: ExportState = { status: 'idle' };
  isDarkMode = false;
  currentDashboard = '';

  private readonly DASHBOARD_NAMES: Record<string, string> = {
    sla: 'SLA',
    variance: 'Variance',
    categorization: 'Categorization',
    smileback: 'SmileBack',
  };

  constructor(
    readonly ticketAnalysisService: TicketAnalysisService,
    private readonly themeService: ThemeService,
    private readonly destroyRef: DestroyRef,
    private readonly snackBar: MatSnackBar,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  private resolveCurrentDashboard(): void {
    const path = this.route.firstChild?.snapshot.url[0]?.path ?? '';
    this.currentDashboard = this.DASHBOARD_NAMES[path] ?? '';
  }

  ngOnInit(): void {
    this.resolveCurrentDashboard();
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.resolveCurrentDashboard());

    const exportInstanceId = this.route.snapshot.queryParamMap.get('exportInstanceId');
    if (exportInstanceId) {
      this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
      this.ticketAnalysisService.getExportDownload(exportInstanceId).subscribe({
        next: ({ sasUrl }) => {
          window.location.href = sasUrl;
        },
      });
    }

    this.isDarkMode = this.themeService.isInDarkMode();
    this.themeService.themeSubject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.isDarkMode = this.themeService.isInDarkMode()));

    let prevStatus: ExportStatus = 'idle';
    this.ticketAnalysisService.exportState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      if (state.status === 'pending' && prevStatus !== 'pending') {
        this.snackBar.openFromComponent(SnackbarComponent, {
          data: { message: "Export in progress. You'll be notified when it's ready." },
          duration: 5000,
        });
      }
      prevStatus = state.status;
      this.exportState = state;
    });

    this.ticketAnalysisService.downloadReady$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ sasUrl }) => {
      window.location.href = sasUrl;
      this.ticketAnalysisService.resetExportState();
    });
  }

  onExportClick(): void {
    const { status, instanceId } = this.exportState;
    if (status === 'completed' && instanceId) {
      this.ticketAnalysisService.getExportDownload(instanceId).subscribe({
        next: ({ sasUrl }) => {
          window.location.href = sasUrl;
          this.ticketAnalysisService.resetExportState();
        },
      });
      return;
    }
    if (status === 'failed' || status === 'idle') {
      this.ticketAnalysisService.exportRequested$.next();
    }
  }

  get exportTooltip(): string {
    switch (this.exportState.status) {
      case 'pending':
        return 'Exporting...';
      case 'completed':
        return 'Download ready — click to save';
      case 'failed':
        return 'Export failed — click to retry';
      default:
        return 'Export CSV';
    }
  }
}
